import React, { useEffect, useState } from "react";
import { API_BASE } from "../apiConfig";
import "./CaregiversNursesManagement.css";
import Modal from "./Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faTrashAlt,
  faUserNurse,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  fetchLocations,
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
} from "../utils/api";
import PageHeader from "./layout/PageHeader";
import { Stethoscope } from "lucide-react";
import TimeInputWithAmPm from "./TimeInputWithAmPm";

const CaregiversNursesManagement = ({
  activeLocation,
  locations: headerLocations = [],
  onChangeLocation,
  companyDisplayName,
  currentUser: headerUser,
  onLogout,
  onLock,
  onUserUpdated,
  roleFilter,
  pageTitle,
}) => {
  const [caregiversNursesData, setCaregiversNursesData] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteConfirmModalOpen, setIsDeleteConfirmModalOpen] =
    useState(false);
  const [currentCaregiver, setCurrentCaregiver] = useState(null);
  const [caregiverToDelete, setCaregiverToDelete] = useState(null);
  const [loading, setLoading] = useState(true); // Start as true for initial load
  const [assigningSelf, setAssigningSelf] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [caregiverWarnings, setCaregiverWarnings] = useState([]);
  const [emailPreview, setEmailPreview] = useState(""); // Kept to satisfy build, though unused

  // State for Add Caregiver Modal
  const [newCaregiverName, setNewCaregiverName] = useState("");
  const [newCaregiverEmail, setNewCaregiverEmail] = useState("");
  const [newCaregiverPhone, setNewCaregiverPhone] = useState("");
  const [newCaregiverEmployeeId, setNewCaregiverEmployeeId] = useState("");
  const [newCaregiverLocations, setNewCaregiverLocations] = useState([]);
  const [newCaregiverSpecialization, setNewCaregiverSpecialization] =
    useState("");
  const [newCaregiverNotes, setNewCaregiverNotes] = useState("");
  const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  const [newCaregiverAvailabilitySlots, setNewCaregiverAvailabilitySlots] =
    useState([]);
  const normalizedRoleFilter = String(roleFilter || "").toLowerCase();
  const roleFilterLabel = roleFilter || "Caregivers/Nurses";
  const defaultRole = roleFilter || "Caregiver";
  const [newCaregiverRole, setNewCaregiverRole] = useState(defaultRole);
  const [newCaregiverStreet, setNewCaregiverStreet] = useState("");
  const [newCaregiverApt, setNewCaregiverApt] = useState("");
  const [newCaregiverCity, setNewCaregiverCity] = useState("");
  const [newCaregiverState, setNewCaregiverState] = useState("");
  const [newCaregiverZip, setNewCaregiverZip] = useState("");
  const [newCaregiverCountry, setNewCaregiverCountry] = useState("USA");

  // Validation State
  const [formErrors, setFormErrors] = useState({});
  const [editFormErrors, setEditFormErrors] = useState({});
  const [touched, setTouched] = useState({});

  // State for Edit Caregiver Modal
  const [editCaregiverName, setEditCaregiverName] = useState("");
  const [editCaregiverEmail, setEditCaregiverEmail] = useState("");
  const [editCaregiverPhone, setEditCaregiverPhone] = useState("");
  const [editCaregiverEmployeeId, setEditCaregiverEmployeeId] = useState("");
  const [editCaregiverLocations, setEditCaregiverLocations] = useState([]);
  const [editCaregiverSpecialization, setEditCaregiverSpecialization] =
    useState("");
  const [editCaregiverNotes, setEditCaregiverNotes] = useState("");
  const [editCaregiverAvailabilitySlots, setEditCaregiverAvailabilitySlots] =
    useState([]);

  const parseDayTimeAvailability = (availabilityStr = "") => {
    if (!availabilityStr) return [];
    const slots = [];

    const normalizeDay = (day) => {
      const d = (day || "").trim().toLowerCase();
      if (d.startsWith("mon")) return "Mon";
      if (d.startsWith("tue")) return "Tue";
      if (d.startsWith("wed")) return "Wed";
      if (d.startsWith("thu")) return "Thu";
      if (d.startsWith("fri")) return "Fri";
      if (d.startsWith("sat")) return "Sat";
      if (d.startsWith("sun")) return "Sun";
      return null;
    };

    const pushSlot = (day, start, end, allDay) => {
      const normDay = normalizeDay(day);
      if (!normDay) return;
      const s = start || "09:00";
      const e = end || "17:00";
      const is24 = allDay === true || (s === "00:00" && e === "23:59");
      slots.push({
        day: normDay,
        start: is24 ? "00:00" : s,
        end: is24 ? "23:59" : e,
        allDay: is24,
      });
    };

    // 1) Already an array of objects
    if (Array.isArray(availabilityStr)) {
      availabilityStr.forEach((s) =>
        pushSlot(s.day, s.start, s.end, s.allDay),
      );
    }

    // 2) JSON string
    if (!slots.length && availabilityStr.trim().startsWith("[")) {
      try {
        const parsed = JSON.parse(availabilityStr);
        if (Array.isArray(parsed)) {
          parsed.forEach((s) =>
            pushSlot(s.day, s.start, s.end, s.allDay),
          );
        }
      } catch (_) {
        // ignore parse error, fallback to other formats
      }
    }

    // 3) Semicolon-delimited "Mon 09:00-17:00; Tue 10:00-18:00; Wed 24hrs"
    if (!slots.length) {
      const parts = availabilityStr
        .split(";")
        .map((p) => p.trim())
        .filter(Boolean);
      const timeRegex =
        /(?<day>Mon|Tue|Wed|Thu|Fri|Sat|Sun|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s+(?<start>\d{2}:\d{2})\s*-\s*(?<end>\d{2}:\d{2})?/i;
      const hr24Regex =
        /(Mon|Tue|Wed|Thu|Fri|Sat|Sun|Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday)\s+24hrs?/i;
      parts.forEach((part) => {
        const timeM = part.match(timeRegex);
        if (timeM?.groups) {
          pushSlot(timeM.groups.day, timeM.groups.start, timeM.groups.end, false);
          return;
        }
        const hr24M = part.match(hr24Regex);
        if (hr24M) {
          pushSlot(hr24M[1], "00:00", "23:59", true);
        }
      });
    }

    // 4) Legacy combined days: "Mon, Tue 09:00-17:00"
    if (!slots.length) {
      const legacy = availabilityStr.match(
        /([A-Za-z,\s]+)\s+(\d{2}:\d{2})\s*-\s*(\d{2}:\d{2})/,
      );
      if (legacy) {
        const days = legacy[1]
          .split(",")
          .map((d) => d.trim())
          .filter(Boolean);
        const start = legacy[2] || "09:00";
        const end = legacy[3] || "17:00";
        days.forEach((day) => pushSlot(day, start, end, false));
      }
    }

    return slots;
  };

  const toggleDay = (setter) => (day, checked) => {
    setter((prev) => {
      if (checked) {
        if (prev.find((s) => s.day === day)) return prev;
        return [
          ...prev,
          { day, start: "09:00", end: "17:00", allDay: false },
        ];
      }
      return prev.filter((s) => s.day !== day);
    });
  };

  const toggleSlot24hrs = (setter) => (day, checked) => {
    setter((prev) =>
      prev.map((s) =>
        s.day === day
          ? {
              ...s,
              allDay: !!checked,
              start: checked ? "00:00" : "09:00",
              end: checked ? "23:59" : "17:00",
            }
          : s,
      ),
    );
  };

  const updateSlotTime = (setter) => (day, key, value) =>
    setter((prev) =>
      prev.map((s) => (s.day === day ? { ...s, [key]: value } : s)),
    );

  const toggleNewDay = toggleDay(setNewCaregiverAvailabilitySlots);
  const toggleEditDay = toggleDay(setEditCaregiverAvailabilitySlots);
  const toggleNewSlot24hrs = toggleSlot24hrs(setNewCaregiverAvailabilitySlots);
  const toggleEditSlot24hrs = toggleSlot24hrs(setEditCaregiverAvailabilitySlots);
  const updateNewSlotTime = updateSlotTime(setNewCaregiverAvailabilitySlots);
  const updateEditSlotTime = updateSlotTime(setEditCaregiverAvailabilitySlots);
  const [editCaregiverRole, setEditCaregiverRole] = useState(defaultRole);

  useEffect(() => {
    setNewCaregiverRole(defaultRole);
    setEditCaregiverRole(defaultRole);
  }, [defaultRole]);

  const getDisplayRole = (accountType) => {
    if (roleFilter) return defaultRole;
    return accountType || "Caregiver";
  };

  const normalizeText = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");

  const getLocationCompanyId = (loc) =>
    loc?.companyId?._id || loc?.companyId?.id || loc?.companyId;

  const isHqLocation = (loc) => {
    if (!loc) return false;
    if (loc.isHQ || loc.isHq || loc.isHeadquarters) return true;
    const normalizedName = normalizeText(loc.name);
    const normalizedCompany = normalizeText(companyDisplayName);
    if (!normalizedName || !normalizedCompany) return false;
    return (
      normalizedName === normalizedCompany ||
      normalizedName === `${normalizedCompany} hq` ||
      normalizedName === `${normalizedCompany} headquarters`
    );
  };

  const applyHqSelection = (selectedIds) => {
    const selectedSet = new Set(selectedIds || []);
    const selectedLocations = locations.filter((loc) =>
      selectedSet.has(loc.id || loc._id),
    );
    const hq = selectedLocations.find(isHqLocation);
    if (!hq) return selectedIds;
    const hqCompanyId = getLocationCompanyId(hq);
    const allIds = locations
      .filter((loc) => {
        if (!hqCompanyId) return true;
        return getLocationCompanyId(loc) === hqCompanyId;
      })
      .map((loc) => loc.id || loc._id)
      .filter(Boolean);
    return Array.from(new Set(allIds));
  };

  const getLocationName = (locValue) => {
    if (!locValue) return "—";
    const locId = locValue.id || locValue._id || locValue;
    const match = locations.find((l) => (l.id || l._id) === locId);
    return match?.name || locValue.name || locId || "—";
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        const locs = await fetchLocations();
        setLocations(locs);
      } catch (err) {
        console.error("Failed to load locations", err);
      }
    };
    loadData();
  }, []);

  // Fetch caregivers/nurses on mount
  useEffect(() => {
    const loadCaregivers = async () => {
      // Wait for auth to be ready
      const token = localStorage.getItem("token");
      const userStr = localStorage.getItem("user");

      if (!token || !userStr) {
        console.log("⚠️ [Caregivers] Auth not ready, skipping fetch");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Fetch ALL users, then filter client-side for Caregiver/Nurse
        // Backend now supports comma-separated accountType via $in operator
        const response = await fetch(`${API_BASE}/api/users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error("Failed to fetch caregivers");
        }

        const users = await response.json();

        // Double filter for safety (in case backend returns all users)
        const caregiversOrNurses = users
          .filter((u) => {
            const acct = String(u.accountType || "").toLowerCase();
            const isCareTeam =
              acct.includes("caregiver") || acct.includes("nurse");
            if (!isCareTeam) return false;
            if (!normalizedRoleFilter) return true;
            return acct.includes(normalizedRoleFilter);
          })
          .map((u) => ({
            ...u,
            availability: u.availability || u.availabilityString || "",
          }));
        setCaregiversNursesData(caregiversOrNurses);
        console.log(`✅ Loaded ${caregiversOrNurses.length} caregivers/nurses`);
      } catch (err) {
        console.error("Failed to load caregivers:", err);
        setError("Failed to load caregivers. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    // Small delay to ensure auth is hydrated from localStorage
    const timer = setTimeout(loadCaregivers, 100);
    return () => clearTimeout(timer);
  }, [roleFilter, normalizedRoleFilter, refreshTrigger]);

  useEffect(() => {
    if (locations.length === 1 && newCaregiverLocations.length === 0) {
      const onlyId = locations[0].id || locations[0]._id;
      if (onlyId) setNewCaregiverLocations([onlyId]);
    }
  }, [locations, newCaregiverLocations]);

  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
    setNewCaregiverRole(defaultRole);
    setFormErrors({});
    setTouched({});
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    setError("");
    setNewCaregiverName("");
    setNewCaregiverEmail("");
    setNewCaregiverPhone("");
    setNewCaregiverEmployeeId("");
    setNewCaregiverLocations([]);
    setNewCaregiverSpecialization("");
    setNewCaregiverNotes("");
    setNewCaregiverAvailabilitySlots([]);
    setNewCaregiverRole(defaultRole);
    setNewCaregiverStreet("");
    setNewCaregiverApt("");
    setNewCaregiverCity("");
    setNewCaregiverState("");
    setNewCaregiverZip("");
    setNewCaregiverCountry("USA");
    setFormErrors({});
    setTouched({});
  };

  const validateForm = () => {
    const errors = {};
    if (!newCaregiverName.trim()) errors.name = "Full Name is required";
    if (!newCaregiverEmail.trim()) errors.email = "Email is required";
    if (!defaultRole && !newCaregiverRole) errors.role = "Role is required";
    if (newCaregiverLocations.length === 0)
      errors.location = "Location is required";
    if (!newCaregiverPhone.trim())
      errors.phone = "Phone Number is required";
    if (!newCaregiverStreet.trim())
      errors.street = "Street Address is required";
    if (!newCaregiverCity.trim()) errors.city = "City is required";
    if (!newCaregiverState.trim()) errors.state = "State is required";
    if (!newCaregiverZip.trim()) errors.zip = "ZIP Code is required";
    const validSlots = newCaregiverAvailabilitySlots.filter(
      (s) => s.day && s.start && s.end,
    );
    if (!validSlots.length) errors.availability = "Select at least one day";
    return errors;
  };

  const isFormValid = () => {
    const errors = validateForm();
    return Object.keys(errors).length === 0;
  };

  const handleCreateCaregiver = async (e) => {
    e.preventDefault();
    const errors = validateForm();
    setFormErrors(errors);
    setTouched({
      name: true,
      email: true,
      phone: true,
      role: true,
      location: true,
      street: true,
      city: true,
      state: true,
      zip: true,
    });

    if (Object.keys(errors).length > 0) {
      setError("Please fix the highlighted errors before continuing.");
      return;
    }

    const normalizeEmail = (value) =>
      String(value || "")
        .trim()
        .toLowerCase();
    try {
      setLoading(true);
      setError("");
      // Determine Company ID
      let companyId = null;
      if (newCaregiverLocations.length > 0) {
        const locObj = locations.find(
          (l) => (l.id || l._id) === newCaregiverLocations[0],
        );
        if (locObj && locObj.companyId) companyId = locObj.companyId;
      }
      if (!companyId) {
        const userStr = localStorage.getItem("user");
        if (userStr) {
          const u = JSON.parse(userStr);
          companyId = u.companyId;
        }
      }

      const validSlots = newCaregiverAvailabilitySlots.filter(
        (s) => s.day && s.start && s.end,
      );
      const availabilityString = validSlots
        .map((slot) =>
          slot.allDay
            ? `${slot.day} 24hrs`
            : `${slot.day} ${slot.start}-${slot.end}`,
        )
        .join("; ");
      const payload = {
        name: newCaregiverName,
        email: newCaregiverEmail,
        phone: newCaregiverPhone,
        employeeId: newCaregiverEmployeeId,
        specialization: newCaregiverSpecialization,
        notes: newCaregiverNotes,
        availability: availabilityString,
        locations: newCaregiverLocations,
        status: "Active",
        accountType: defaultRole || newCaregiverRole,
        role: "ReadOnly", // Backend Expects "ReadOnly", not "Read Only"
        companyId: companyId,
        address: {
          street: newCaregiverStreet,
          aptSuiteUnit: newCaregiverApt,
          city: newCaregiverCity,
          state: newCaregiverState,
          zipCode: newCaregiverZip,
          country: newCaregiverCountry || "USA",
        },
      };

      console.log("Creating Caregiver Payload:", payload);
      const data = await createUser(payload);
      const responseWarnings = Array.isArray(data?.warnings)
        ? data.warnings.filter(Boolean)
        : [];
      setCaregiverWarnings(responseWarnings);
      const saved = data.user || data;
      const savedWithAvail = {
        ...saved,
        availability: saved.availability || availabilityString,
      };
      setCaregiversNursesData((prev) => {
        const savedId = savedWithAvail.id || savedWithAvail._id;
        if (!savedId) return [...prev, savedWithAvail];
        const exists = prev.find((item) => (item.id || item._id) === savedId);
        if (exists) {
          return prev.map((item) =>
            (item.id || item._id) === savedId ? savedWithAvail : item,
          );
        }
        return [...prev, savedWithAvail];
      });
      setSuccessMessage(
        `${defaultRole || "Caregiver"} created successfully. Login credentials sent via email.`,
      );
      setTimeout(() => setSuccessMessage(""), 5000);
      handleCloseAddModal();
    } catch (err) {
      const apiMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message;
      const isDupEmail =
        err?.response?.data?.error === "EMAIL_ALREADY_EXISTS" ||
        (apiMsg || "").includes("E11000") ||
        (apiMsg || "").toLowerCase().includes("duplicate");
      if (isDupEmail) {
        try {
          const users = await fetchUsers();
          const existing = (users || []).find(
            (u) =>
              normalizeEmail(u.email) === normalizeEmail(newCaregiverEmail),
          );
          if (existing) {
            const existingLocs = (existing.locations || []).map((loc) =>
              typeof loc === "object" ? loc.id || loc._id : loc,
            );
            const mergedLocations = Array.from(
              new Set([
                ...(existingLocs || []),
                ...(newCaregiverLocations || []),
              ]),
            ).filter(Boolean);
            const updatePayload = {
              name: newCaregiverName || existing.name,
              email: newCaregiverEmail,
              phone: newCaregiverPhone || existing.phone,
              employeeId: newCaregiverEmployeeId || existing.employeeId,
              specialization:
                newCaregiverSpecialization || existing.specialization,
              notes: newCaregiverNotes || existing.notes,
              availability:
                newCaregiverAvailabilitySlots.length > 0
                  ? newCaregiverAvailabilitySlots
                      .filter((s) => s.day && s.start && s.end)
                      .map((slot) => `${slot.day} ${slot.start}-${slot.end}`)
                      .join("; ")
                  : existing.availability,
              locations: mergedLocations,
              accountType: defaultRole || newCaregiverRole,
            };
            const updated = await updateUser(
              existing.id || existing._id,
              updatePayload,
            );
            const resolved = {
              ...existing,
              ...updated,
            };
            setCaregiversNursesData((prev) =>
              prev.map((c) =>
                (c.id || c._id) === (existing.id || existing._id)
                  ? resolved
                  : c,
              ),
            );
            setSuccessMessage(
              `${defaultRole || "Caregiver"} updated to include this role and locations.`,
            );
            setTimeout(() => setSuccessMessage(""), 5000);
            handleCloseAddModal();
            return;
          }
        } catch (linkErr) {
          console.error("Link existing caregiver failed:", linkErr);
        }
      }
      const friendly = isDupEmail
        ? "This email address is already in use. Please use a different email."
        : apiMsg;
      console.error("Create caregiver failed:", apiMsg, err?.response?.data);
      setCaregiverWarnings([]);
      setError(friendly || "Failed to create caregiver");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = (caregiver) => {
    setCurrentCaregiver(caregiver);
    setEditCaregiverName(caregiver.name);
    setEditCaregiverEmail(caregiver.email);
    setEditCaregiverPhone(caregiver.phone);
    setEditCaregiverEmployeeId(caregiver.employeeId);
    const locs = (caregiver.locations || []).map((loc) =>
      typeof loc === "object" ? loc.id || loc._id : loc,
    );
    setEditCaregiverLocations(locs.filter(Boolean));
    setEditCaregiverSpecialization(caregiver.specialization);
    setEditCaregiverNotes(caregiver.notes);
    setEditCaregiverAvailabilitySlots(
      parseDayTimeAvailability(caregiver.availability || ""),
    );
    setEditCaregiverRole(defaultRole || caregiver.accountType || "Caregiver");
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentCaregiver(null);
    setError("");
    setEditFormErrors({});
    setEditCaregiverName("");
    setEditCaregiverEmail("");
    setEditCaregiverPhone("");
    setEditCaregiverEmployeeId("");
    setEditCaregiverLocations([]);
    setEditCaregiverSpecialization("");
    setEditCaregiverNotes("");
    setEditCaregiverAvailabilitySlots([]);
    setEditCaregiverRole(defaultRole);
  };

  const handleUpdateCaregiver = async (e) => {
    e.preventDefault();
    const editErrors = {};
    if (!editCaregiverPhone.trim())
      editErrors.phone = "Phone Number is required";
    setEditFormErrors(editErrors);
    if (Object.keys(editErrors).length > 0) {
      setError("Please fix the highlighted errors before continuing.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const id = currentCaregiver.id || currentCaregiver._id;
      const validSlots = (editCaregiverAvailabilitySlots || []).filter(
        (s) => s.day && s.start && s.end,
      );
      const availabilityString = validSlots
        .map((slot) =>
          slot.allDay
            ? `${slot.day} 24hrs`
            : `${slot.day} ${slot.start}-${slot.end}`,
        )
        .join("; ");
      const payload = {
        name: editCaregiverName,
        email: editCaregiverEmail,
        phone: editCaregiverPhone,
        employeeId: editCaregiverEmployeeId,
        specialization: editCaregiverSpecialization,
        notes: editCaregiverNotes,
        availability: availabilityString,
        locations: editCaregiverLocations,
        accountType: defaultRole || editCaregiverRole,
      };
      const updated = await updateUser(id, payload);
      setCaregiversNursesData((prev) =>
        prev.map((c) =>
          (c.id || c._id) === id
            ? { ...c, ...updated, availability: availabilityString }
            : c,
        ),
      );
      handleCloseEditModal();
    } catch (err) {
      setError(err.message || "Failed to update caregiver");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteConfirmModal = (caregiver) => {
    setCaregiverToDelete(caregiver);
    setIsDeleteConfirmModalOpen(true);
  };

  const handleCloseDeleteConfirmModal = () => {
    setIsDeleteConfirmModalOpen(false);
    setCaregiverToDelete(null);
  };

  // Admin can assign themselves as Caregiver/Nurse (same email, same password for PWA/mobile)
  const currentUserHasRole = () => {
    const acct = String(headerUser?.accountType || "").toLowerCase();
    return acct.includes(normalizedRoleFilter);
  };

  const currentUserIsAdmin = () => {
    const role = String(headerUser?.role || "").toLowerCase();
    return (
      role.includes("full_access") ||
      role.includes("full access") ||
      role.includes("editor") ||
      role.includes("admin") ||
      role.includes("view_only") ||
      role.includes("read only") ||
      role.includes("readonly")
    );
  };

  const showAssignSelfButton =
    headerUser?.id || headerUser?._id ? currentUserIsAdmin() && !currentUserHasRole() : false;

  const handleAssignSelf = async () => {
    const id = headerUser?.id || headerUser?._id;
    if (!id) return;
    try {
      setAssigningSelf(true);
      setError("");
      setSuccessMessage("");
      const updatedUser = await updateUser(id, { accountType: defaultRole });
      if (typeof onUserUpdated === "function") {
        onUserUpdated(updatedUser);
      }
      setRefreshTrigger((t) => t + 1);
      setSuccessMessage(
        `You can now sign in to the mobile/PWA app with the same email and password as ${defaultRole}.`,
      );
    } catch (err) {
      setError(err?.message || "Failed to assign yourself as " + defaultRole);
    } finally {
      setAssigningSelf(false);
    }
  };

  const handleDeleteCaregiver = async () => {
    try {
      setLoading(true);
      setError("");
      const id = caregiverToDelete.id || caregiverToDelete._id;
      await deleteUser(id);
      setCaregiversNursesData((prev) =>
        prev.filter((c) => (c.id || c._id) !== id),
      );
      handleCloseDeleteConfirmModal();
    } catch (err) {
      setError(err.message || "Failed to delete caregiver");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="caregivers-nurses-management-container page-shell">
      <PageHeader
        title={pageTitle || roleFilterLabel}
        subtitle={`Manage ${roleFilterLabel.toLowerCase()} and keep staffing records current.`}
        icon={<Stethoscope className="h-6 w-6 text-white" />}
        activeLocation={activeLocation}
        locations={headerLocations}
        onChangeLocation={onChangeLocation}
        companyDisplayName={companyDisplayName}
        currentUser={headerUser}
        onLogout={onLogout}
        onLock={onLock}
      />
      <div className="page-actions">
        {showAssignSelfButton && (
          <button
            className="button assign-self-button"
            onClick={handleAssignSelf}
            disabled={assigningSelf}
          >
            {assigningSelf
              ? "Assigning..."
              : `Assign myself as ${defaultRole}`}
          </button>
        )}
        <button
          className="button add-caregiver-button"
          onClick={handleOpenAddModal}
        >
          {`+ Add ${defaultRole || "Caregiver/Nurse"}`}
        </button>
      </div>
      {error && <div className="error-banner">{error}</div>}
      {successMessage && <div className="success-banner">{successMessage}</div>}
      {caregiverWarnings.length > 0 && (
        <div className="warning-banner">
          <div className="warning-banner-title">
            Caregiver saved with setup warnings:
          </div>
          <ul className="warning-banner-list">
            {caregiverWarnings.map((warning, idx) => (
              <li key={`${warning}-${idx}`}>{warning}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="caregivers-nurses-table-container">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Role</th>
              <th>Phone</th>
              <th>Location</th>
              <th>Employee ID</th>
              <th>Specialization</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && caregiversNursesData.length === 0 ? (
              <tr>
                <td
                  colSpan="8"
                  style={{ textAlign: "center", padding: "40px" }}
                >
                  Loading caregivers...
                </td>
              </tr>
            ) : caregiversNursesData.length === 0 ? (
              <tr>
                <td
                  colSpan="8"
                  style={{
                    textAlign: "center",
                    padding: "40px",
                    color: "#999",
                  }}
                >
                  No {roleFilterLabel.toLowerCase()} found. Click "Add" to
                  create one.
                </td>
              </tr>
            ) : (
              caregiversNursesData.map((caregiver) => (
                <tr key={caregiver.id || caregiver._id}>
                  <td>
                    <div className="caregiver-info">
                      <div className="caregiver-avatar">
                        {caregiver.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </div>
                      <div>
                        <span className="caregiver-name">{caregiver.name}</span>
                        <br />
                        <span className="caregiver-role">
                          {getDisplayRole(caregiver.accountType)}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>{getDisplayRole(caregiver.accountType)}</td>
                  <td>{caregiver.phone || "—"}</td>
                  <td>
                    {(caregiver.locations || [])
                      .map((l) => getLocationName(l))
                      .join(", ")}
                  </td>
                  <td>{caregiver.employeeId}</td>
                  <td>{caregiver.specialization}</td>
                  <td>
                    <span
                      className={`status-badge ${(
                        caregiver.status || "active"
                      ).toLowerCase()}`}
                    >
                      {caregiver.status || "Active"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="action-button edit-button"
                      onClick={() => handleOpenEditModal(caregiver)}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button
                      className="action-button delete-button"
                      onClick={() => handleOpenDeleteConfirmModal(caregiver)}
                    >
                      <FontAwesomeIcon icon={faTrashAlt} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add New Caregiver Modal - REDESIGNED */}
      {isAddModalOpen && (
        <div className="modal-overlay" onClick={handleCloseAddModal}>
          <div
            className="appt-modal"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: "700px" }}
          >
            <div className="modal-head">
              <h3>{`Add New ${defaultRole || "Caregiver / Nurse"}`}</h3>
            </div>

            <div className="modal-content-scroll">
              <div className="modal-form-container">
                {/* --- PERSONAL DETAILS --- */}
                <div className="form-section-card">
                  <div className="form-section-header">
                    <h4 className="form-section-title">
                      <FontAwesomeIcon
                        icon={faUserNurse}
                        className="text-indigo-500"
                      />
                      Personal &amp; Professional Info
                    </h4>
                  </div>
                  <div className="form-grid-2-col">
                    <div className="form-group">
                      <label className="form-label">
                        Full Name <span className="req-star">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${
                          formErrors.name ? "input-error" : ""
                        }`}
                        value={newCaregiverName}
                        onChange={(e) => {
                          setNewCaregiverName(e.target.value);
                          if (touched.name)
                            setFormErrors({ ...formErrors, name: "" });
                        }}
                        onBlur={() => setTouched({ ...touched, name: true })}
                        placeholder="e.g. Sarah Connor"
                      />
                      {formErrors.name && (
                        <div className="error-msg">{formErrors.name}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Email Address <span className="req-star">*</span>
                      </label>
                      <input
                        type="email"
                        className={`form-input ${
                          formErrors.email ? "input-error" : ""
                        }`}
                        value={newCaregiverEmail}
                        onChange={(e) => {
                          setNewCaregiverEmail(e.target.value);
                          if (touched.email)
                            setFormErrors({ ...formErrors, email: "" });
                        }}
                        onBlur={() => setTouched({ ...touched, email: true })}
                        placeholder="e.g. sarah@example.com"
                      />
                      {formErrors.email && (
                        <div className="error-msg">{formErrors.email}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Phone Number <span className="req-star">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${formErrors.phone ? "input-error" : ""}`}
                        value={newCaregiverPhone}
                        onChange={(e) => {
                          setNewCaregiverPhone(e.target.value);
                          if (formErrors.phone)
                            setFormErrors({ ...formErrors, phone: "" });
                        }}
                        placeholder="e.g. 555-123-4567"
                      />
                      {formErrors.phone && (
                        <div className="error-msg">{formErrors.phone}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Employee ID</label>
                      <input
                        type="text"
                        className="form-input"
                        value={newCaregiverEmployeeId}
                        onChange={(e) =>
                          setNewCaregiverEmployeeId(e.target.value)
                        }
                        placeholder="e.g. EMP-001"
                      />
                    </div>
                  </div>
                </div>

                {/* --- ROLE & LOCATION --- */}
                <div className="form-section-card">
                  <div className="form-section-header">
                    <h4 className="form-section-title">
                      <FontAwesomeIcon
                        icon={faCheckCircle}
                        className="text-indigo-500"
                      />
                      Role &amp; Location Assignment
                    </h4>
                  </div>
                  <div className="form-grid-2-col">
                    <div className="form-group">
                      <label className="form-label">
                        Role <span className="req-star">*</span>
                      </label>
                      {roleFilter ? (
                        <div className="form-input read-only">
                          {defaultRole}
                        </div>
                      ) : (
                        <select
                          className={`form-select ${
                            formErrors.role ? "input-error" : ""
                          }`}
                          value={newCaregiverRole}
                          onChange={(e) => {
                            setNewCaregiverRole(e.target.value);
                            if (touched.role)
                              setFormErrors({ ...formErrors, role: "" });
                          }}
                          onBlur={() => setTouched({ ...touched, role: true })}
                        >
                          <option value="Caregiver">Caregiver</option>
                          <option value="Nurse">Nurse</option>
                        </select>
                      )}
                      {formErrors.role && (
                        <div className="error-msg">{formErrors.role}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Assigned Locations <span className="req-star">*</span>
                      </label>
                      <select
                        multiple
                        className={`form-select ${
                          formErrors.location ? "input-error" : ""
                        }`}
                        value={newCaregiverLocations}
                        onChange={(e) => {
                          const selected = Array.from(
                            e.target.selectedOptions,
                          ).map((opt) => opt.value);
                          setNewCaregiverLocations(applyHqSelection(selected));
                          if (touched.location)
                            setFormErrors({ ...formErrors, location: "" });
                        }}
                        onBlur={() =>
                          setTouched({ ...touched, location: true })
                        }
                      >
                        {locations.length > 0 ? (
                          locations.map((loc) => (
                            <option
                              key={loc.id || loc._id}
                              value={loc.id || loc._id}
                            >
                              {loc.name}
                            </option>
                          ))
                        ) : (
                          <option disabled>No locations available</option>
                        )}
                      </select>
                      {formErrors.location && (
                        <div className="error-msg">{formErrors.location}</div>
                      )}
                    </div>

                    {/* Address Information */}
                    <div className="form-group span-full">
                      <h5 className="form-subtitle">Address Information</h5>
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        Street Address <span className="req-star">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${formErrors.street ? "input-error" : ""}`}
                        value={newCaregiverStreet}
                        onChange={(e) => {
                          setNewCaregiverStreet(e.target.value);
                          if (touched.street)
                            setFormErrors({ ...formErrors, street: "" });
                        }}
                        onBlur={() => setTouched({ ...touched, street: true })}
                        placeholder="123 Main St"
                      />
                      {formErrors.street && (
                        <div className="error-msg">{formErrors.street}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Apt/Suite/Unit</label>
                      <input
                        type="text"
                        className="form-input"
                        value={newCaregiverApt}
                        onChange={(e) => setNewCaregiverApt(e.target.value)}
                        placeholder="Apt 4B"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        City <span className="req-star">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${formErrors.city ? "input-error" : ""}`}
                        value={newCaregiverCity}
                        onChange={(e) => {
                          setNewCaregiverCity(e.target.value);
                          if (touched.city)
                            setFormErrors({ ...formErrors, city: "" });
                        }}
                        onBlur={() => setTouched({ ...touched, city: true })}
                        placeholder="City"
                      />
                      {formErrors.city && (
                        <div className="error-msg">{formErrors.city}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        State <span className="req-star">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${formErrors.state ? "input-error" : ""}`}
                        value={newCaregiverState}
                        onChange={(e) => {
                          setNewCaregiverState(e.target.value);
                          if (touched.state)
                            setFormErrors({ ...formErrors, state: "" });
                        }}
                        onBlur={() => setTouched({ ...touched, state: true })}
                        placeholder="State"
                      />
                      {formErrors.state && (
                        <div className="error-msg">{formErrors.state}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">
                        ZIP Code <span className="req-star">*</span>
                      </label>
                      <input
                        type="text"
                        className={`form-input ${formErrors.zip ? "input-error" : ""}`}
                        value={newCaregiverZip}
                        onChange={(e) => {
                          setNewCaregiverZip(e.target.value);
                          if (touched.zip)
                            setFormErrors({ ...formErrors, zip: "" });
                        }}
                        onBlur={() => setTouched({ ...touched, zip: true })}
                        placeholder="ZIP"
                      />
                      {formErrors.zip && (
                        <div className="error-msg">{formErrors.zip}</div>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        className="form-input"
                        value={newCaregiverCountry}
                        onChange={(e) => setNewCaregiverCountry(e.target.value)}
                        placeholder="Country"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Specialization</label>
                      <input
                        type="text"
                        className="form-input"
                        value={newCaregiverSpecialization}
                        onChange={(e) =>
                          setNewCaregiverSpecialization(e.target.value)
                        }
                        placeholder="e.g. Pediatric, Geriatric"
                      />
                    </div>

                    <div className="form-group span-full">
                      <label className="form-label">
                        Availability (Days &amp; Time){" "}
                        <span className="req-star">*</span>
                      </label>
                      <div className="availability-grid">
                        <div className="days-checkboxes">
                          {DAYS.map((day) => (
                            <label key={day} className="day-pill">
                              <input
                                type="checkbox"
                                checked={newCaregiverAvailabilitySlots.some(
                                  (s) => s.day === day,
                                )}
                                onChange={(e) => {
                                  toggleNewDay(day, e.target.checked);
                                  setTouched({
                                    ...touched,
                                    availability: true,
                                  });
                                }}
                              />
                              {day}
                            </label>
                          ))}
                        </div>
                        {newCaregiverAvailabilitySlots.length > 0 && (
                          <div className="availability-table">
                            <div className="availability-table-header four-col">
                              <span>Day</span>
                              <span>24hrs</span>
                              <span>Start</span>
                              <span>End</span>
                              <span></span>
                            </div>
                            {newCaregiverAvailabilitySlots.map((slot) => {
                              const is24 = slot.allDay === true;
                              return (
                                <div
                                  key={slot.day}
                                  className="availability-row four-col"
                                >
                                  <span className="day-chip">{slot.day}</span>
                                  <label className="checkbox-24hrs">
                                    <input
                                      type="checkbox"
                                      checked={is24}
                                      onChange={(e) =>
                                        toggleNewSlot24hrs(
                                          slot.day,
                                          e.target.checked,
                                        )
                                      }
                                    />
                                    <span>24hrs</span>
                                  </label>
                                  {is24 ? (
                                    <span className="all-day-label" style={{ gridColumn: "span 2" }}>
                                      24 hours
                                    </span>
                                  ) : (
                                    <>
                                      <TimeInputWithAmPm
                                        value={slot.start}
                                        onChange={(v) =>
                                          updateNewSlotTime(slot.day, "start", v)
                                        }
                                        className="form-input"
                                      />
                                      <TimeInputWithAmPm
                                      value={slot.end}
                                      onChange={(v) =>
                                        updateNewSlotTime(slot.day, "end", v)
                                      }
                                        className="form-input"
                                      />
                                    </>
                                  )}
                                  <button
                                    type="button"
                                    className="remove-slot-btn"
                                    onClick={() =>
                                      toggleNewDay(slot.day, false)
                                    }
                                  >
                                    ✕
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                      {formErrors.availability && (
                        <div className="error-msg">
                          {formErrors.availability}
                        </div>
                      )}
                    </div>

                    <div className="form-group span-full">
                      <label className="form-label">Notes</label>
                      <textarea
                        className="form-textarea"
                        value={newCaregiverNotes}
                        onChange={(e) => setNewCaregiverNotes(e.target.value)}
                        placeholder="Additional notes..."
                      ></textarea>
                    </div>
                  </div>
                </div>

                {error && <div className="modal-error">{error}</div>}
              </div>
            </div>

            <div className="modal-fixed-footer">
              <button className="ghost-btn" onClick={handleCloseAddModal}>
                Cancel
              </button>
              <button
                className="primary-btn"
                onClick={handleCreateCaregiver}
                disabled={loading || !isFormValid()}
              >
                {loading ? "Creating..." : "Create Caregiver"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Caregiver Modal - Simplified UI for now, focusing on Add Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        title="Edit Caregiver"
      >
        <form onSubmit={handleUpdateCaregiver}>
          <div className="form-group-row">
            <div className="form-group">
              <label>Full Name *</label>
              <input
                type="text"
                value={editCaregiverName}
                onChange={(e) => setEditCaregiverName(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Email Address *</label>
              <input
                type="email"
                value={editCaregiverEmail}
                onChange={(e) => setEditCaregiverEmail(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label>
                Phone Number <span className="req-star">*</span>
              </label>
              <input
                type="text"
                className={editFormErrors.phone ? "input-error" : ""}
                value={editCaregiverPhone}
                onChange={(e) => {
                  setEditCaregiverPhone(e.target.value);
                  if (editFormErrors.phone)
                    setEditFormErrors({ ...editFormErrors, phone: "" });
                }}
              />
              {editFormErrors.phone && (
                <div className="error-msg">{editFormErrors.phone}</div>
              )}
            </div>
            <div className="form-group">
              <label>Employee ID</label>
              <input
                type="text"
                value={editCaregiverEmployeeId}
                onChange={(e) => setEditCaregiverEmployeeId(e.target.value)}
              />
            </div>
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label>Role *</label>
              {roleFilter ? (
                <div className="form-input read-only">{defaultRole}</div>
              ) : (
                <select
                  value={editCaregiverRole}
                  onChange={(e) => setEditCaregiverRole(e.target.value)}
                >
                  <option value="Caregiver">Caregiver</option>
                  <option value="Nurse">Nurse</option>
                </select>
              )}
            </div>
            <div className="form-group">
              <label>Locations *</label>
              <select
                multiple
                value={editCaregiverLocations}
                onChange={(e) => {
                  const selected = Array.from(e.target.selectedOptions).map(
                    (opt) => opt.value,
                  );
                  setEditCaregiverLocations(applyHqSelection(selected));
                }}
              >
                {locations.map((loc) => (
                  <option key={loc.id || loc._id} value={loc.id || loc._id}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group-row">
            <div className="form-group">
              <label>Specialization</label>
              <input
                type="text"
                value={editCaregiverSpecialization}
                onChange={(e) => setEditCaregiverSpecialization(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Availability (Days &amp; Time)</label>
              <div className="availability-grid">
                <div className="days-checkboxes">
                  {DAYS.map((day) => (
                    <label key={day} className="day-pill">
                      <input
                        type="checkbox"
                        checked={editCaregiverAvailabilitySlots.some(
                          (s) => s.day === day,
                        )}
                        onChange={(e) => toggleEditDay(day, e.target.checked)}
                      />
                      {day}
                    </label>
                  ))}
                </div>
                {editCaregiverAvailabilitySlots.length > 0 && (
                  <div className="availability-table">
                    <div className="availability-table-header four-col">
                      <span>Day</span>
                      <span>24hrs</span>
                      <span>Start</span>
                      <span>End</span>
                      <span></span>
                    </div>
                    {editCaregiverAvailabilitySlots.map((slot) => {
                      const is24 = slot.allDay === true;
                      return (
                        <div
                          key={slot.day}
                          className="availability-row four-col"
                        >
                          <span className="day-chip">{slot.day}</span>
                          <label className="checkbox-24hrs">
                            <input
                              type="checkbox"
                              checked={is24}
                              onChange={(e) =>
                                toggleEditSlot24hrs(
                                  slot.day,
                                  e.target.checked,
                                )
                              }
                            />
                            <span>24hrs</span>
                          </label>
                          {is24 ? (
                            <span className="all-day-label" style={{ gridColumn: "span 2" }}>
                              24 hours
                            </span>
                          ) : (
                            <>
                              <TimeInputWithAmPm
                                value={slot.start}
                                onChange={(v) =>
                                  updateEditSlotTime(slot.day, "start", v)
                                }
                                className="form-input"
                              />
                              <TimeInputWithAmPm
                                value={slot.end}
                              onChange={(v) =>
                                updateEditSlotTime(slot.day, "end", v)
                              }
                                className="form-input"
                              />
                            </>
                          )}
                          <button
                            type="button"
                            className="remove-slot-btn"
                            onClick={() => toggleEditDay(slot.day, false)}
                          >
                            ✕
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea
                rows="3"
                value={editCaregiverNotes}
                onChange={(e) => setEditCaregiverNotes(e.target.value)}
              ></textarea>
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="button cancel-button"
              onClick={handleCloseEditModal}
            >
              CANCEL
            </button>
            <button
              type="submit"
              className="button update-button"
              disabled={loading}
            >
              UPDATE CAREGIVER
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Caregiver Confirmation Modal */}
      <Modal
        isOpen={isDeleteConfirmModalOpen}
        onClose={handleCloseDeleteConfirmModal}
        title="Delete Caregiver"
      >
        <div className="delete-confirmation-content">
          <div className="warning-icon">!</div>
          <p>
            Are you sure you want to delete caregiver "
            <strong>{caregiverToDelete?.name}</strong>"?
          </p>
          <p>
            This will deactivate their account and they won't be able to access
            the caregiver app.
          </p>
        </div>
        <div className="modal-actions">
          <button
            type="button"
            className="button cancel-button"
            onClick={handleCloseDeleteConfirmModal}
          >
            CANCEL
          </button>
          <button
            type="button"
            className="button delete-confirm-button"
            onClick={handleDeleteCaregiver}
          >
            DELETE CAREGIVER
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default CaregiversNursesManagement;
